import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { ChoiceField } from "./pickers";
import { Contacts } from "./Contacts";
import { saveJournalFile } from "./coffre";
import { listDocuments, type FxDoc } from "./facturx";
import { Modal } from "./Modal";
import { canonStage, loadSuivi, nextStage, prevStage, saveStages, saveSuivi, STAGE_COLORS, stageList, stageOf, type PipelineStage, type RelationStatus, type Suivi, type SuiviDeal, type SuiviPast } from "./suivi";

export const RELATIONS: { value: RelationStatus; label: string }[] = [
  { value: "prospect", label: "Prospect" },
  { value: "client", label: "Client" },
  { value: "former", label: "Ancien client" },
];
type Bag = { suivi: Suivi | null; error: string; run: (body: Record<string, unknown>) => Promise<boolean> };
const Live = createContext<Bag | null>(null);

export function SuiviScope({ ficheId, onStatus, children }: { ficheId: number; onStatus: (status: RelationStatus) => void; children: ReactNode }) {
  const [suivi, setSuivi] = useState<Suivi | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setSuivi(null);
    setError("");
    void loadSuivi(ficheId)
      .then((next) => {
        if (!alive) return;
        setSuivi(next);
        onStatus(next.relation_status);
      })
      .catch((reason: unknown) => {
        if (alive) setError(reason instanceof Error ? reason.message : "Suivi indisponible");
      });
    return () => {
      alive = false;
    };
  }, [ficheId]);

  useEffect(() => {
    const bump = () => {
      void loadSuivi(ficheId).then((next) => {
        setSuivi(next);
        onStatus(next.relation_status);
      }).catch(() => undefined);
    };
    window.addEventListener("crm-suivi-refresh", bump);
    return () => window.removeEventListener("crm-suivi-refresh", bump);
  }, [ficheId]);

  async function run(body: Record<string, unknown>) {
    setError("");
    try {
      const next = await saveSuivi(ficheId, body);
      setSuivi(next);
      onStatus(next.relation_status);
      return true;
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Enregistrement impossible");
      return false;
    }
  }

  return <Live.Provider value={{ suivi, error, run }}>{children}</Live.Provider>;
}

export function useLive(): Bag {
  const bag = useContext(Live);
  if (!bag) throw new Error("Suivi hors fiche");
  return bag;
}

export function StatusPills() {
  const { suivi, run } = useLive();
  const current = suivi?.relation_status || "prospect";
  return (
    <div className="crm-pill flex rounded-full p-1 text-xs font-semibold">
      {RELATIONS.map((item) => (
        <button
          key={item.value}
          type="button"
          className={current === item.value ? "crm-fill rounded-full px-4 py-2 text-white" : "rounded-full px-4 py-2"}
          onClick={() => void run({ action: "relation", relation_status: item.value })}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

export function FeedButton({ folder, ownerId }: { folder: string; ownerId: number }) {
  const { suivi, error, run } = useLive();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const lines = suivi?.feed || [];
  async function archive() {
    if (!folder || lines.length === 0 || busy) return;
    setBusy(true);
    setNote("");
    try {
      await saveJournalFile(folder, ownerId, journalText(lines, suivi?.deal, suivi?.relation_status, stageList(suivi?.stages)));
      await run({ action: "clear" });
      setNote("Archivé dans le Coffre. Le journal est vide.");
    } catch (reason: unknown) {
      setNote(reason instanceof Error ? reason.message : "Archivage impossible");
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <button type="button" className="crm-pill rounded-full px-4 py-2 text-xs font-semibold" onClick={() => setOpen(true)}>Journal</button>
      {open ? (
        <Modal onClose={() => setOpen(false)} sheetClass="crm-pop flex max-h-[80vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
          {(close) => (
            <>
              <div>
                <h2 className="text-lg font-semibold">Journal</h2>
              </div>
              <p className="mt-1 text-xs text-slate-500">L’actualité de la fiche. Archiver écrit l’affaire et ces lignes dans le Coffre, puis vide le journal.</p>
              {error ? <p className="mt-2 text-sm text-rose-700">{error}</p> : null}
              <ul className="crm-scroll mt-4 min-h-0 flex-1 space-y-2 overflow-auto">
                {lines.length === 0 ? <li className="text-sm text-slate-400">Rien pour l’instant.</li> : null}
                {lines.map((line, index) => (
                  <li key={`${line.created_at}-${index}`} className="rounded-xl border border-slate-200 px-3 py-2">
                    <p className="text-sm">{line.body}</p>
                    <p className="mt-1 text-xs text-slate-400">{[line.actor_name, showDateTime(line.created_at)].filter(Boolean).join(" · ")}</p>
                  </li>
                ))}
              </ul>
              {note ? <p className="mt-3 text-sm text-slate-500">{note}</p> : null}
              <button type="button" className="mt-4 h-9 justify-self-start rounded-full crm-fill px-4 text-sm font-semibold text-white disabled:opacity-50" disabled={busy || lines.length === 0 || folder === ""} onClick={() => void archive()}>
                {busy ? "Archivage…" : "Archiver dans le Coffre"}
              </button>
            </>
          )}
        </Modal>
      ) : null}
    </>
  );
}

export function ContactsButton({ fiche }: { fiche: { id: number; name: string; email: string; phone: string; facturx_client_id: number } }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="crm-pill rounded-full px-4 py-2 text-xs font-semibold" onClick={() => setOpen(true)}>Contacts</button>
      {open ? (
        <Modal onClose={() => setOpen(false)} sheetClass="crm-pop max-h-[85vh] w-full max-w-lg overflow-auto rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
          {(close) => <Contacts fiche={fiche} onClose={close} />}
        </Modal>
      ) : null}
    </>
  );
}

export function StageBar({ companyId, clientId, mine = true }: { companyId: number; clientId: number; mine?: boolean }) {
  const { suivi } = useLive();
  return <DealBoard suivi={suivi} companyId={companyId} clientId={clientId} mine={mine} />;
}

function showDateTime(iso: string): string {
  const clean = iso.replace("T", " ");
  const [date, time] = clean.split(" ");
  return [showDate(date || ""), (time || "").slice(0, 5)].filter(Boolean).join(" ");
}

function journalText(lines: { body: string; actor_name?: string; created_at: string }[], deal?: SuiviDeal, relation?: string, stages: PipelineStage[] = []): string {
  const when = new Date().toLocaleString("fr-FR", { dateStyle: "full", timeStyle: "short" });
  const stage = stageOf(canonStage(deal?.status || "", stages), stages)?.label || "";
  const amount = deal && deal.amount > 0 ? euros(deal.amount) : "";
  const status = RELATIONS.find((item) => item.value === relation)?.label || "";
  const affaire = [deal?.title.trim() || "Sans intitulé", stage, amount, deal?.quote_id ? `Devis n° ${deal.quote_id}` : ""].filter(Boolean).join(" · ");
  const body = [...lines].reverse().map((line) => `- ${showDateTime(line.created_at)}${line.actor_name ? ` · ${line.actor_name}` : ""}\n  ${line.body}`).join("\n\n");
  return `# Journal CRM\n\nArchivé le ${when}.\n\nStatut : ${status || "Prospect"}\n\n## Affaire\n\n${affaire}\n\n## Actualité\n\n${body || "Aucune ligne."}\n`;
}

function euros(value: number): string {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
}

function showDate(iso: string): string {
  const [year, month, day] = iso.split("-");
  if (!year || !month || !day) return "";
  return `${day}/${month}/${year}`;
}

function DealBoard({ suivi, companyId, clientId, mine = true }: { suivi: Suivi | null; companyId: number; clientId: number; mine?: boolean }) {
  const { error, run } = useLive();
  const [deal, setDeal] = useState<SuiviDeal | null>(suivi?.deal ? { ...suivi.deal, status: canonStage(suivi.deal.status) } : null);
  const [quotes, setQuotes] = useState<FxDoc[]>([]);
  const [moving, setMoving] = useState(false);
  const [dropAsk, setDropAsk] = useState<number | "open" | null>(null);
  const [view, setView] = useState<"board" | "list">(() => (window.localStorage.getItem("crm_pipeline_view") === "list" ? "list" : "board"));
  const [editing, setEditing] = useState(false);
  const stages = stageList(suivi?.stages);
  useEffect(() => setDeal(suivi?.deal ? { ...suivi.deal, status: canonStage(suivi.deal.status, stageList(suivi.stages)) } : null), [suivi?.deal, suivi?.stages]);
  useEffect(() => {
    if (companyId <= 0 || clientId <= 0) {
      setQuotes([]);
      return;
    }
    let alive = true;
    void listDocuments(companyId, clientId)
      .then((data) => { if (alive) setQuotes((data.documents || []).filter((row) => row.doc_type === "quote")); })
      .catch(() => { if (alive) setQuotes([]); });
    return () => { alive = false; };
  }, [companyId, clientId]);
  const current = deal;
  const quoteLabel = current ? quotes.find((row) => row.id === current.quote_id) : undefined;
  function payload(next: SuiviDeal) {
    return { action: "deal", ...next, quote_label: quoteLabel ? (quoteLabel.number || `Devis ${quoteLabel.id}`) : "" };
  }
  async function commit(next: SuiviDeal) {
    if (!suivi) return false;
    setDeal(next);
    const ok = await run(payload(next));
    if (!ok) setDeal({ ...suivi.deal, status: canonStage(suivi.deal.status, stages) });
    return ok;
  }
  async function move(status: string) {
    if (!current || moving) return;
    setMoving(true);
    try {
      await commit({ ...current, status });
    } finally {
      setMoving(false);
    }
  }
  const history = suivi?.history || [];
  function choose(next: "board" | "list") {
    setView(next);
    window.localStorage.setItem("crm_pipeline_view", next);
  }
  const card = (stageCode: string) => current && canonStage(current.status, stages) === stageCode ? (
    <DealCard current={current} suivi={suivi} quotes={quotes} clientId={clientId} error={error} moving={moving} dropAsk={dropAsk} stages={stages} onDeal={setDeal} onCommit={commit} onMove={(status) => void move(status)} onDropAsk={setDropAsk} onRun={run} />
  ) : null;
  return (
    <div className="min-w-0">
      <div className="mb-3 flex items-center justify-end gap-2">
        <div className="crm-pill flex rounded-full p-1 text-xs font-semibold">
          <button type="button" className={view === "board" ? "crm-fill rounded-full px-3 py-1 text-white" : "rounded-full px-3 py-1"} onClick={() => choose("board")}>Kanban</button>
          <button type="button" className={view === "list" ? "crm-fill rounded-full px-3 py-1 text-white" : "rounded-full px-3 py-1"} onClick={() => choose("list")}>Liste</button>
        </div>
        {mine ? <button type="button" className="crm-pill rounded-full px-3 py-1.5 text-xs font-semibold" onClick={() => setEditing(true)}>Étapes</button> : null}
      </div>
      {view === "list" ? (
        <ul className="grid gap-2">
          {stages.map((stage) => {
            const here = current ? canonStage(current.status, stages) === stage.code : false;
            return (
              <li key={stage.code} className="crm-card overflow-hidden rounded-2xl border border-slate-200" style={{ boxShadow: `inset 3px 0 0 ${stage.color}` }}>
                <div className="flex items-center gap-2 px-3 py-2">
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: stage.color }} />
                  <p className="min-w-0 flex-1 truncate text-sm font-semibold">{stage.label}</p>
                  {!here && current ? <button type="button" className="crm-pill rounded-full px-3 py-1 text-xs font-semibold" disabled={moving} onClick={() => void move(stage.code)}>Mettre ici</button> : null}
                </div>
                {here ? <div className="border-t border-slate-200 p-3">{card(stage.code)}</div> : null}
                {stage.kind === "won" && history.length > 0 ? <HistoryList history={history} dropAsk={dropAsk} onDropAsk={setDropAsk} onRun={run} /> : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="overflow-x-auto">
          <div className="grid items-stretch gap-3" style={{ gridTemplateColumns: `repeat(${stages.length}, minmax(11rem, 1fr))`, minWidth: `${stages.length * 12}rem` }}>
            {stages.map((stage) => {
              const here = current ? canonStage(current.status, stages) === stage.code : false;
              return (
                <section key={stage.code} className="crm-card flex min-h-52 flex-col overflow-hidden rounded-2xl border border-slate-200 shadow-[0_10px_28px_rgb(15_23_42/7%)]" style={{ boxShadow: `inset 0 3px 0 ${stage.color}, 0 10px 28px rgb(15 23 42 / 7%)` }}>
                  <h3 className="crm-head flex min-h-12 shrink-0 items-center gap-2 border-b px-3 py-2 text-xs font-semibold leading-tight">
                    <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: stage.color }} />
                    {stage.label}
                  </h3>
                  <div className="flex min-h-0 flex-1 flex-col gap-2 p-2">
                    {here ? card(stage.code) : null}
                    {stage.kind === "won" && history.length > 0 ? <HistoryList history={history} dropAsk={dropAsk} onDropAsk={setDropAsk} onRun={run} /> : null}
                    {!here && !(stage.kind === "won" && history.length > 0) ? <p className="m-auto text-xs text-slate-400">—</p> : null}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      )}
      {editing ? (
        <Modal onClose={() => setEditing(false)} sheetClass="crm-pop max-h-[85vh] w-full max-w-lg overflow-auto rounded-2xl border border-slate-200 p-5">
          {(close) => <StageSheet stages={stages} onClose={close} />}
        </Modal>
      ) : null}
    </div>
  );
}

function DealCard({ current, suivi, quotes, clientId, error, moving, dropAsk, stages, onDeal, onCommit, onMove, onDropAsk, onRun }: {
  current: SuiviDeal;
  suivi: Suivi | null;
  quotes: FxDoc[];
  clientId: number;
  error: string;
  moving: boolean;
  dropAsk: number | "open" | null;
  stages: PipelineStage[];
  onDeal: (deal: SuiviDeal) => void;
  onCommit: (deal: SuiviDeal) => Promise<boolean> | void;
  onMove: (status: string) => void;
  onDropAsk: (id: number | "open" | null) => void;
  onRun: (body: Record<string, unknown>) => Promise<boolean>;
}) {
  const next = nextStage(current.status, stages);
  const prev = prevStage(current.status, stages);
  const nextLabel = stageOf(next || "", stages)?.label || "";
  const prevLabel = stageOf(prev || "", stages)?.label || "";
  const kind = stageOf(canonStage(current.status, stages), stages)?.kind;
  const lost = stages.find((item) => item.kind === "lost");
  return (
    <article className="flex min-h-0 flex-1 flex-col gap-2 rounded-xl border border-slate-200 p-2">
      <input className="crm-field h-8 rounded-lg border px-2 text-xs" value={current.title} placeholder="Intitulé" onChange={(event) => onDeal({ ...current, title: event.target.value })} onBlur={() => { if (suivi && current.title !== suivi.deal.title) onCommit(current); }} />
      <input className="crm-field h-8 rounded-lg border px-2 text-xs" type="number" min="0" step="0.01" value={current.amount || ""} placeholder="Montant €" onChange={(event) => onDeal({ ...current, amount: Number(event.target.value) })} onBlur={() => { if (suivi && current.amount !== suivi.deal.amount) onCommit(current); }} />
      <input className="crm-field h-8 rounded-lg border px-2 text-xs" type="number" min="0" max="100" step="1" value={current.probability || ""} placeholder="Probabilité %" onChange={(event) => onDeal({ ...current, probability: Math.max(0, Math.min(100, Number(event.target.value) || 0)) })} onBlur={() => { if (suivi && current.probability !== suivi.deal.probability) onCommit(current); }} />
      <input className="crm-field h-8 rounded-lg border px-2 text-xs" type="date" value={current.expected_on || ""} aria-label="Date de clôture prévue" onChange={(event) => onDeal({ ...current, expected_on: event.target.value })} onBlur={() => { if (suivi && current.expected_on !== suivi.deal.expected_on) onCommit(current); }} />
      {kind === "lost" ? <input className="crm-field h-8 rounded-lg border px-2 text-xs" value={current.loss_reason || ""} placeholder="Motif de perte" onChange={(event) => onDeal({ ...current, loss_reason: event.target.value })} onBlur={() => { if (suivi && current.loss_reason !== suivi.deal.loss_reason) onCommit(current); }} /> : null}
      {clientId > 0 ? (
        <ChoiceField
          value={String(current.quote_id || 0)}
          placeholder="Devis"
          options={[{ value: "0", label: "Aucun devis" }, ...quotes.map((row) => ({ value: String(row.id), label: row.number || `Devis ${row.id}` }))]}
          onChange={(nextId) => onCommit({ ...current, quote_id: Number(nextId) })}
        />
      ) : null}
      {error ? <p className="text-[11px] text-rose-700">{error}</p> : null}
      <div className="mt-auto flex flex-col gap-1.5 pt-1">
        {next ? (
          <button type="button" className="rounded-full crm-fill px-2 py-1.5 text-xs font-semibold text-white disabled:opacity-50" disabled={moving} onMouseDown={(event) => event.preventDefault()} onClick={() => onMove(next)}>
            → {nextLabel}
          </button>
        ) : null}
        {kind === "open" && lost ? (
          <button type="button" className="crm-pill rounded-full px-2 py-1.5 text-xs font-semibold disabled:opacity-50" disabled={moving} onMouseDown={(event) => event.preventDefault()} onClick={() => onMove(lost.code)}>
            → {lost.label}
          </button>
        ) : null}
        {prev ? (
          <button type="button" className="crm-pill rounded-full px-2 py-1.5 text-xs font-semibold disabled:opacity-50" disabled={moving} onMouseDown={(event) => event.preventDefault()} onClick={() => onMove(prev)}>
            ← {prevLabel}
          </button>
        ) : null}
        {kind === "won" ? (
          <>
            <button type="button" className="crm-pill rounded-full px-2 py-1.5 text-xs font-semibold" onMouseDown={(event) => event.preventDefault()} onClick={() => { void Promise.resolve(onCommit(current)).then((ok) => { if (ok) void onRun({ action: "shelve" }); }); }}>
              Historique
            </button>
            <button type="button" className="crm-pill rounded-full px-2 py-1.5 text-xs font-semibold text-rose-700 disabled:opacity-50" disabled={moving} onMouseDown={(event) => event.preventDefault()} onClick={() => { if (dropAsk === "open") void onRun({ action: "drop" }); else onDropAsk("open"); }}>
              {dropAsk === "open" ? "Confirmer la suppression" : "Supprimer"}
            </button>
          </>
        ) : null}
      </div>
    </article>
  );
}

function HistoryList({ history, dropAsk, onDropAsk, onRun }: {
  history: SuiviPast[];
  dropAsk: number | "open" | null;
  onDropAsk: (id: number | "open" | null) => void;
  onRun: (body: Record<string, unknown>) => Promise<boolean>;
}) {
  return (
    <ul className="crm-scroll max-h-28 space-y-1 overflow-auto">
      {history.map((item) => (
        <li key={item.id} className="flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1.5 text-[11px] text-slate-500">
          <span className="min-w-0 flex-1 truncate">{[item.title.trim() || "Sans intitulé", item.amount > 0 ? euros(item.amount) : ""].filter(Boolean).join(" · ")}</span>
          <button type="button" className="shrink-0 font-semibold text-rose-700" onClick={() => { if (dropAsk === item.id) void onRun({ action: "drop", id: item.id }); else onDropAsk(item.id); }}>
            {dropAsk === item.id ? "Oui" : "Suppr."}
          </button>
        </li>
      ))}
    </ul>
  );
}

export function StageSheet({ stages, onClose }: { stages: PipelineStage[]; onClose: () => void }) {
  const [rows, setRows] = useState(stages);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const open = rows.filter((item) => item.kind === "open");
  function patch(code: string, next: Partial<PipelineStage>) {
    setRows(rows.map((item) => (item.code === code ? { ...item, ...next } : item)));
  }
  function shift(code: string, dir: -1 | 1) {
    const list = rows.filter((item) => item.kind === "open");
    const index = list.findIndex((item) => item.code === code);
    const target = index + dir;
    if (index < 0 || target < 0 || target >= list.length) return;
    const swapped = [...list];
    [swapped[index], swapped[target]] = [swapped[target], swapped[index]];
    setRows([...swapped, ...rows.filter((item) => item.kind !== "open")]);
  }
  function add() {
    if (rows.length >= 12) {
      setError("Douze étapes au maximum.");
      return;
    }
    const code = `s${Date.now().toString(36).slice(-6)}`;
    const fresh: PipelineStage = { code, label: "Nouvelle étape", color: STAGE_COLORS[open.length % STAGE_COLORS.length], kind: "open" };
    setRows([...open, fresh, ...rows.filter((item) => item.kind !== "open")]);
    setError("");
  }
  function remove(code: string) {
    if (open.length <= 1) return;
    setRows(rows.filter((item) => item.code !== code));
  }
  async function save() {
    setBusy(true);
    setError("");
    try {
      await saveStages(rows);
      onClose();
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Étapes non enregistrées");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="grid gap-3">
      <h2 className="text-base font-semibold">Étapes du pipeline</h2>
      <ul className="grid gap-2">
        {rows.map((stage) => {
          const index = open.findIndex((item) => item.code === stage.code);
          return (
            <li key={stage.code} className="grid gap-2 rounded-xl border border-slate-200 p-2">
              <div className="flex items-center gap-2">
                <input className="crm-field h-8 min-w-0 flex-1 rounded-lg border px-2 text-sm" value={stage.label} onChange={(event) => patch(stage.code, { label: event.target.value })} />
                {stage.kind === "open" ? (
                  <>
                    <button type="button" className="crm-pill h-8 w-8 rounded-full text-sm" disabled={index <= 0} onClick={() => shift(stage.code, -1)} aria-label="Monter">↑</button>
                    <button type="button" className="crm-pill h-8 w-8 rounded-full text-sm" disabled={index < 0 || index >= open.length - 1} onClick={() => shift(stage.code, 1)} aria-label="Descendre">↓</button>
                    <button type="button" className="crm-pill h-8 rounded-full px-2 text-xs font-semibold text-rose-700" disabled={open.length <= 1} onClick={() => remove(stage.code)}>Retirer</button>
                  </>
                ) : null}
              </div>
              <div className="flex gap-1.5">
                {STAGE_COLORS.map((color) => (
                  <button key={color} type="button" className="h-5 w-5 rounded-full border border-white" style={{ background: color, outline: stage.color === color ? "2px solid #0f172a" : "none" }} aria-label={color} onClick={() => patch(stage.code, { color })} />
                ))}
              </div>
            </li>
          );
        })}
      </ul>
      {error ? <p className="text-xs text-rose-700">{error}</p> : null}
      <div className="flex justify-end gap-2">
        <button type="button" className="crm-pill rounded-full px-3 py-1.5 text-xs font-semibold" disabled={rows.length >= 12} onClick={add}>Ajouter</button>
        <button type="button" className="rounded-full crm-fill px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50" disabled={busy} onClick={() => void save()}>{busy ? "…" : "Enregistrer"}</button>
      </div>
    </div>
  );
}
